@let conversation = conversation$$$.$ | async;
@let paginatorState = messagesPaginator.state$ | async;
@let isFromCurrentUserFn = isFromCurrentUser();

<span id="top-anchor"></span>
@if (conversation) {
	<section
		id="comment"
		class="flex-1 py-8 pb-32 antialiased"
	>
		<div class="mx-auto max-w-3xl px-4">
			@if (paginatorState) {
				@if (commentTitle()) {
					<div class="mb-6 flex items-center justify-between">
						<h2 class="text-base-content flex items-center gap-2 text-lg font-bold lg:text-2xl">
							<span [translate]>{{ commentTitle() }}</span>

							<span>({{ paginatorState.totalNumberOfItems }})</span>
						</h2>

						<div class="flex items-center gap-2">
							@if (isAdmin() && resourceKind() === 'article') {
								<button
									type="button"
									class="btn btn-secondary btn-sm"
									[cdkMenuTriggerFor]="adminArticleMenu"
								>
									<svg
										class="h-4 w-4"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
										></path>
									</svg>
									<span [translate]>Admin</span>
									<svg
										class="ml-1 h-3 w-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M19 9l-7 7-7-7"
										></path>
									</svg>
								</button>
								<ng-template #adminArticleMenu>
									<ul
										class="menu bg-base-100 border-base-200 w-52 rounded-md border p-2 shadow-xl"
										cdkMenu
									>
										<li cdkMenuItem>
											<button
												(click)="editArticle()"
												class="flex items-center gap-2"
											>
												<svg
													class="h-4 w-4"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
													></path>
												</svg>
												<span [translate]>Edit Article</span>
											</button>
										</li>
										<li [cdkMenuTriggerFor]="prefixSubmenu">
											<button class="flex items-center justify-between gap-2">
												<div class="flex items-center gap-2">
													<svg
														class="h-4 w-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															stroke-linecap="round"
															stroke-linejoin="round"
															stroke-width="2"
															d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
														></path>
													</svg>
													<span [translate]>Add Prefix</span>
												</div>
												<svg
													class="h-3 w-3 opacity-60"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M9 5l7 7-7 7"
													></path>
												</svg>
											</button>
										</li>
										<ng-template #prefixSubmenu>
											<ul
												class="menu bg-base-100 border-base-200 w-44 rounded-md border p-2 shadow-xl"
												cdkMenu
											>
												@for (prefix of articleTitlePrefixes; track prefix) {
													<li cdkMenuItem>
														<button
															(click)="addPrefixToTitle(prefix)"
															class="font-mono text-sm"
														>
															{{ prefix }}
														</button>
													</li>
												}
											</ul>
										</ng-template>
										@if (resourceAuthorId()) {
											<li cdkMenuItem>
												<button
													(click)="goToAdminUserPage(resourceAuthorId() ?? '')"
													class="flex items-center gap-2"
												>
													<svg
														class="h-4 w-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															stroke-linecap="round"
															stroke-linejoin="round"
															stroke-width="2"
															d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
														></path>
													</svg>
													<span [translate]>View User</span>
												</button>
											</li>
										}
									</ul>
								</ng-template>
							}
						</div>
					</div>
				}

				<!-- Show More Button -->
				@if (hasMoreMessages()) {
					<div class="mb-4 flex justify-center">
						<button
							type="button"
							class="btn btn-ghost btn-sm"
							(click)="showMoreMessages()"
						>
							<span [translate]>Show more messages</span>
						</button>
					</div>
				}

				<div
					class="flex flex-col-reverse gap-4"
					#commentsContainer
				>
					<!-- Assistant processing indicator -->
					@if (isAssistantProcessing()) {
						<div class="chat chat-start">
							<div class="chat-image avatar">
								<div class="bg-secondary text-secondary-content grid h-10 w-10 place-items-center rounded-full">
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										fill="currentColor"
										class="h-5 w-5"
									>
										<path d="M16.5 7.5h-9v9h9v-9z" />
										<path
											fill-rule="evenodd"
											d="M8.25 2.25A.75.75 0 019 3v.75h2.25V3a.75.75 0 011.5 0v.75H15V3a.75.75 0 011.5 0v.75h.75a3 3 0 013 3v.75H21A.75.75 0 0121 9h-.75v2.25H21a.75.75 0 010 1.5h-.75V15H21a.75.75 0 010 1.5h-.75v.75a3 3 0 01-3 3h-.75V21a.75.75 0 01-1.5 0v-.75h-2.25V21a.75.75 0 01-1.5 0v-.75H9V21a.75.75 0 01-1.5 0v-.75h-.75a3 3 0 01-3-3v-.75H3A.75.75 0 013 15h.75v-2.25H3a.75.75 0 010-1.5h.75V9H3a.75.75 0 010-1.5h.75v-.75a3 3 0 013-3h.75V3a.75.75 0 01.75-.75z"
											clip-rule="evenodd"
										/>
									</svg>
								</div>
							</div>
							<div class="chat-header mb-1">
								<span
									class="mr-2 text-sm font-semibold opacity-70"
									[translate]
									>Assistant</span
								>
							</div>
							<div class="chat-bubble chat-bubble-secondary flex flex-col gap-2 opacity-80">
								<div class="flex items-center gap-2">
									<span class="loading loading-dots loading-sm"></span>
									<span class="text-sm" [translate]>Processing...</span>
									@if (assistantProgress() > 0) {
										<span class="text-sm font-semibold">{{ assistantProgress() | number: '1.0-0' }}%</span>
									}
								</div>
								@if (assistantProgress() > 0) {
									<progress
										class="progress progress-secondary w-full"
										[value]="assistantProgress()"
										max="100"
									></progress>
								}
							</div>
						</div>
					}

					@for (message of processedMessages(); track message?.id) {
						@if (message) {
							@let isOwnMessage = isFromCurrentUserFn(message.authorId);

							<!-- Assistant thinking message: collapsible block -->
							@if (message.kind === 'assistant-thinking') {
								<div
									class="chat chat-start"
									[id]="'message-' + message.id"
								>
									<div class="chat-image avatar">
										<div class="bg-secondary/40 text-secondary-content grid h-10 w-10 place-items-center rounded-full">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 24 24"
												fill="currentColor"
												class="h-5 w-5 opacity-60"
											>
												<path d="M16.5 7.5h-9v9h9v-9z" />
												<path
													fill-rule="evenodd"
													d="M8.25 2.25A.75.75 0 019 3v.75h2.25V3a.75.75 0 011.5 0v.75H15V3a.75.75 0 011.5 0v.75h.75a3 3 0 013 3v.75H21A.75.75 0 0121 9h-.75v2.25H21a.75.75 0 010 1.5h-.75V15H21a.75.75 0 010 1.5h-.75v.75a3 3 0 01-3 3h-.75V21a.75.75 0 01-1.5 0v-.75h-2.25V21a.75.75 0 01-1.5 0v-.75H9V21a.75.75 0 01-1.5 0v-.75h-.75a3 3 0 01-3-3v-.75H3A.75.75 0 013 15h.75v-2.25H3a.75.75 0 010-1.5h.75V9H3a.75.75 0 010-1.5h.75v-.75a3 3 0 013-3h.75V3a.75.75 0 01.75-.75z"
													clip-rule="evenodd"
												/>
											</svg>
										</div>
									</div>
									<div class="chat-header mb-1 opacity-60">
										<span class="mr-2 text-sm font-semibold">Assistant</span>
										<time class="text-xs opacity-50">{{ message.timeCreated | dateAsAgo }}</time>
									</div>
									<div class="max-w-full">
										<details class="collapse collapse-arrow border-base-300 bg-base-200/40 rounded-box border">
											<summary class="collapse-title text-xs font-medium opacity-60">
												<span [translate]>Thinking steps</span>
											</summary>
											<div class="collapse-content">
												<div
													class="prose prose-sm max-w-none opacity-70"
													[markdown-to-html]="message.content"
												></div>
											</div>
										</details>
									</div>
								</div>

								<!-- Assistant response message: rendered with markdown -->
							} @else if (message.kind === 'assistant-response' || message.kind === 'agent') {
								<div
									class="chat chat-start"
									[id]="'message-' + message.id"
								>
									<div class="chat-image avatar">
										<div class="bg-secondary text-secondary-content grid h-10 w-10 place-items-center rounded-full">
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 24 24"
												fill="currentColor"
												class="h-5 w-5"
											>
												<path d="M16.5 7.5h-9v9h9v-9z" />
												<path
													fill-rule="evenodd"
													d="M8.25 2.25A.75.75 0 019 3v.75h2.25V3a.75.75 0 011.5 0v.75H15V3a.75.75 0 011.5 0v.75h.75a3 3 0 013 3v.75H21A.75.75 0 0121 9h-.75v2.25H21a.75.75 0 010 1.5h-.75V15H21a.75.75 0 010 1.5h-.75v.75a3 3 0 01-3 3h-.75V21a.75.75 0 01-1.5 0v-.75h-2.25V21a.75.75 0 01-1.5 0v-.75H9V21a.75.75 0 01-1.5 0v-.75h-.75a3 3 0 01-3-3v-.75H3A.75.75 0 013 15h.75v-2.25H3a.75.75 0 010-1.5h.75V9H3a.75.75 0 010-1.5h.75v-.75a3 3 0 013-3h.75V3a.75.75 0 01.75-.75z"
													clip-rule="evenodd"
												/>
											</svg>
										</div>
									</div>
									<div class="chat-header mb-1">
										<span class="mr-2 font-semibold opacity-70">Assistant</span>
										<time class="text-xs opacity-50">{{ message.timeCreated | dateAsAgo }}</time>
									</div>
									<div class="chat-bubble bg-base-200 text-base-content">
										<div
											class="prose prose-sm max-w-none"
											[markdown-to-html]="message.content"
										></div>
									</div>
								</div>

								<!-- Regular user message -->
							} @else {
								<div
									class="chat group"
									[class.chat-end]="isOwnMessage"
									[class.chat-start]="!isOwnMessage"
									[id]="'message-' + message.id"
								>
									<!-- Avatar -->
									<div class="chat-image avatar">
										<div class="w-10 rounded-full">
											@if (message.avatarUrl) {
												<img
													[src]="message.avatarUrl"
													[alt]="message.authorPublicName"
												/>
											} @else {
												<div class="bg-neutral text-neutral-content grid h-full w-full place-items-center">
													<span class="text-xs">{{ message.authorPublicName | slice: 0 : 2 }}</span>
												</div>
											}
										</div>
									</div>

									<!-- Header: Name & Time -->
									<div class="chat-header mb-1">
										<span class="mr-2 font-semibold opacity-70">{{ message.authorPublicName }}</span>
										<time class="text-xs opacity-50">{{ message.timeCreated | dateAsAgo }}</time>
									</div>

									<!-- Bubble -->
									<div
										class="chat-bubble relative pr-10"
										[class.chat-bubble-primary]="isOwnMessage"
										[class.text-primary-content]="isOwnMessage"
										[class.text-base-content]="!isOwnMessage"
									>
										<!-- Replying To Config -->
										@if (message.replyTo) {
											<div class="border-base-content/20 bg-base-100/10 mb-2 rounded border-l-4 p-2 text-xs">
												<p class="font-bold opacity-70">{{ message.replyTo.authorPublicName }}</p>
												<p class="line-clamp-2 opacity-80">{{ message.replyTo.content }}</p>
											</div>
										}

										<!-- Content -->
										<div class="whitespace-pre-wrap">
											@if (message.content && message.content.length > COMMENT_LENGTH_COLLAPSE && !expandedComments.isSelected(message)) {
												<span [innerHTML]="message.content.slice(0, COMMENT_LENGTH_COLLAPSE) + '...' | newlinesToBr"></span>
												<button
													class="link link-hover mt-1 block text-xs opacity-70"
													(click)="expandedComments.toggle(message)"
													[translate]
												>
													Read more
												</button>
											} @else {
												<span [innerHTML]="message.content | newlinesToBr"></span>
												@if (message.content && message.content.length > COMMENT_LENGTH_COLLAPSE) {
													<button
														class="link link-hover mt-1 block text-xs opacity-70"
														(click)="expandedComments.toggle(message)"
														[translate]
													>
														Read less
													</button>
												}
											}
										</div>
									</div>

									<!-- Footer: Reactions & Actions -->
									<div class="chat-footer mt-1 flex min-h-[1.5rem] items-center gap-2 opacity-80">
										<!-- Reactions -->
										@if (message.reactionsSummary.length > 0) {
											<div class="flex flex-wrap gap-1">
												@for (summary of message.reactionsSummary; track summary.emoji) {
													<button
														(click)="setReaction(message, summary.emoji)"
														class="badge badge-sm hover:badge-outline gap-1 transition-all"
														[class.badge-primary]="summary.userReacted"
														[class.badge-ghost]="!summary.userReacted"
													>
														{{ summary.emoji }} {{ summary.count > 1 ? summary.count : '' }}
													</button>
												}
											</div>
										}

										<!-- Action Buttons (Visible on Group Hover) -->
										<div class="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
											<button
												class="btn btn-ghost btn-xs btn-square"
												title="Reply"
												(click)="initiateReply(message)"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
													stroke-width="1.5"
													stroke="currentColor"
													class="h-4 w-4"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
													/>
												</svg>
											</button>
											<button
												class="btn btn-ghost btn-xs btn-square"
												title="React"
												(click)="toggleReactionPicker(message.id)"
											>
												<svg
													xmlns="http://www.w3.org/2000/svg"
													fill="none"
													viewBox="0 0 24 24"
													stroke-width="1.5"
													stroke="currentColor"
													class="h-4 w-4"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z"
													/>
												</svg>
											</button>

											<div class="dropdown dropdown-top dropdown-end">
												<div
													tabindex="0"
													role="button"
													class="btn btn-ghost btn-xs btn-square"
												>
													<svg
														xmlns="http://www.w3.org/2000/svg"
														fill="none"
														viewBox="0 0 24 24"
														stroke-width="1.5"
														stroke="currentColor"
														class="h-4 w-4"
													>
														<path
															stroke-linecap="round"
															stroke-linejoin="round"
															d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
														/>
													</svg>
												</div>
												<ul
													tabindex="0"
													class="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow"
												>
													<li><a (click)="initiateReply(message)">Reply</a></li>
													<li>
														<a
															(click)="removeComment(message.id)"
															class="text-error"
															[translate]
															>Remove</a
														>
													</li>
												</ul>
											</div>
										</div>
									</div>

									<!-- Reaction Picker -->
									@if (activeReactionPickerMessageId() === message.id) {
										<div class="chat-footer mt-2 w-full">
											<div class="join bg-base-100 border-base-200 rounded-full border p-1 shadow-lg">
												@for (reaction of availableReactions; track reaction) {
													<button
														class="btn btn-ghost btn-xs btn-circle text-lg"
														[class.btn-active]="message.currentUserReactionEmoji === reaction"
														(click)="setReaction(message, reaction)"
													>
														{{ reaction }}
													</button>
												}
											</div>
										</div>
									}
								</div>
							}
						}
					}
				</div>
			} @else {
				@if (displayNoCommentsMessage()) {
					<div class="hero py-12">
						<div class="hero-content text-center">
							<div class="max-w-md">
								<p
									class="text-base-content/50"
									[translate]
								>
									No messages yet. Start the conversation!
								</p>
							</div>
						</div>
					</div>
				}
			}
			<span id="bottom-anchor"></span>
		</div>
	</section>

	<!-- message input at bottom -->
	<div class="border-base-200 bg-base-100/95 sticky right-0 bottom-0 left-0 w-full backdrop-blur">
		<div class="mx-auto max-w-3xl px-4 py-4">
			<!-- Reply Context -->
			@if (replyingToMessage(); as activeReplyTarget) {
				<div class="alert border-base-200 mb-2 border py-2 text-sm shadow-sm">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						fill="none"
						viewBox="0 0 24 24"
						class="stroke-info h-6 w-6 shrink-0"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						></path>
					</svg>
					<div class="min-w-0 flex-1">
						<h3 class="text-xs font-bold"><span [translate]>Replying to:</span>&nbsp; {{ activeReplyTarget.authorPublicName }}</h3>
						<div class="truncate text-xs opacity-70">{{ activeReplyTarget.content }}</div>
					</div>
					<button
						class="btn btn-sm btn-ghost btn-circle"
						(click)="clearReplyTarget()"
					>
						✕
					</button>
				</div>
			}

			<form
				(submit)="postComment()"
				class="flex items-end gap-2"
			>
				@if (usersRepository.currentProfile()) {
					@if (conversation && conversation.config && conversation.config.richText) {
						<span [translate]>not implemented yet</span>
					} @else {
						<div class="flex-1">
							<textarea
								#commentTextarea
								id="comment"
								rows="1"
								class="textarea textarea-bordered focus:textarea-primary w-full resize-none leading-normal"
								placeholder="{{ commentPlaceholderText() }}"
								required
								name="commentContent"
								[(ngModel)]="newCommentContent"
								[maxlength]="MAX_COMMENT_LENGTH"
								(keydown)="onEnterKeyDown($event)"
							></textarea>
						</div>
					}
					<button
						type="submit"
						class="btn btn-primary"
						[disabled]="!newCommentContent().trim() || isSubmittingMessage() || isAssistantProcessing()"
					>
						@if (isSubmittingMessage()) {
							<span class="loading loading-spinner loading-xs"></span>
						}
						<span [translate]>{{ commentButtonText() }}</span>
					</button>
				} @else {
					<div class="flex-1">
						<textarea
							class="textarea textarea-bordered w-full resize-none"
							placeholder="You need to be logged in to send messages"
							disabled
						></textarea>
					</div>
					<button
						class="btn btn-disabled"
						disabled
						[translate]
					>
						{{ commentButtonText() }}
					</button>
				}
			</form>
		</div>
	</div>
}
